import type { ReactNode } from 'react';
import {
  sharePct,
  type RevenueByServiceTypeRow,
  type RevenueKpis,
} from '@servwell/shared';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { EmptyState } from '../../ui/EmptyState';
import { Change } from '../../ui/Change';
import { IconReports } from '../../icons';
import { money } from '../../money';
import { Mark } from './Mark';
import { opensInPeriod } from './RevenueDrill';
import type { BasisWords } from './basisWords';
import { REVENUE_SECTION } from './onScreen';

/**
 * Revenue by service type: one row per type of the shop's with revenue in the period or the
 * same days a year earlier, largest first, then "No service type" and "Not tied to a job", in
 * the order the server sent them.
 *
 * ⚠️ No column sorts, on purpose: the leftover rows are always LAST, and a header click that
 * sorted them in among the types would put a row that is not a kind of work between two that
 * are.
 *
 * The footer is the SERVER's headline (`kpis`), not a sum the browser makes: the page draws
 * what the server decided, and the server's claim is that its rows add up to that figure to the
 * cent, in both years, which `report.revenue`'s own tests pin.
 *
 * A period with nothing invoiced says so ("Nothing invoiced in Sep 1–28, 2026", or under
 * Collected "Nothing collected in …") and still states the same days a year earlier, whatever
 * they held (spec, States); a type that earned then keeps its row below the notice.
 *
 * Under Collected a row, and the total, can be below zero (refunds outrunning what arrived):
 * the amount keeps its sign, and the share bar draws nothing for it while its words give the
 * share; a share of a total at or below zero is "—" (`sharePct`).
 *
 * Each row with something in the period opens its invoices (ticket 06); one there only for a
 * year earlier does not, and neither does a year-earlier figure or an average.
 */

/** What an empty period still says about the same days a year earlier. */
function yearEarlierNote(
  { total, jobs }: RevenueKpis,
  words: BasisWords,
  yearEarlierDays: string,
): string {
  if (total.yearEarlierCents === 0 && jobs.yearEarlierCount === 0) {
    return `Nothing in ${yearEarlierDays} either. Pick a longer period, or come back ${words.fillsOnce}.`;
  }
  const count = jobs.yearEarlierCount;
  const onJobs =
    count === 0
      ? ''
      : count === 1
        ? ' on 1 job'
        : ` on ${count.toLocaleString('en-US')} jobs`;
  return `${yearEarlierDays}, the same days a year earlier: ${money(total.yearEarlierCents)} ${words.verb}${onJobs}.`;
}

/** The share as a bar and its words. The bar draws only what it can; the words say it all. */
function Share({
  row,
  totalCents,
}: {
  row: RevenueByServiceTypeRow;
  totalCents: number;
}) {
  const pct = sharePct(row.cents, totalCents, 1);
  if (pct === null) return <>—</>;
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="flex h-2 w-16 overflow-hidden rounded-full bg-surface-2">
        <Mark of={row} className="h-full" width={`${Math.min(100, Math.max(0, pct))}%`} />
      </span>
      <span className="tabular-nums">{pct.toFixed(1)}%</span>
    </span>
  );
}

export function ByServiceType({
  rows,
  kpis,
  words,
  days,
  yearEarlierDays,
  onOpen,
}: {
  rows: RevenueByServiceTypeRow[];
  /** The headline: what each share is of, and what the footer states. */
  kpis: RevenueKpis;
  /** The basis the rows are on, in words: the columns' names, the empty notice, the openers. */
  words: BasisWords;
  /** The period in words, e.g. "Jul 1–31, 2026". */
  days: string;
  /** The same days a year earlier in words, e.g. "Jul 1–31, 2025". */
  yearEarlierDays: string;
  /** A row was opened: its invoices, in the drill. */
  onOpen: (row: RevenueByServiceTypeRow) => void;
}) {
  // Nothing invoiced (or collected) and no job in the period: the notice, above any row a year
  // earlier left.
  const nothingInPeriod = kpis.total.cents === 0 && kpis.jobs.count === 0;
  const notice = (
    <EmptyState
      icon={IconReports}
      title={`Nothing ${words.verb} in ${days}`}
      description={yearEarlierNote(kpis, words, yearEarlierDays)}
    />
  );
  const columns: DataTableColumn<RevenueByServiceTypeRow>[] = [
    {
      key: 'type',
      label: 'Service type',
      cell: (r) => (
        <span className="inline-flex items-center gap-2">
          <Mark of={r} className="size-2.5 shrink-0 rounded-full" />
          <span className={r.kind === 'type' ? undefined : 'text-muted-foreground'}>
            {r.label}
          </span>
        </span>
      ),
    },
    {
      key: 'jobs',
      label: words.jobsColumn,
      align: 'right',
      // "—" on "Not tied to a job": there is no job to count.
      cell: (r) => (r.jobs === null ? '—' : r.jobs.toLocaleString('en-US')),
      footer: () => kpis.jobs.count.toLocaleString('en-US'),
    },
    {
      key: 'cents',
      label: words.label,
      align: 'right',
      cell: (r) => money(r.cents),
      footer: () => money(kpis.total.cents),
    },
    {
      key: 'average',
      label: words.averageJob,
      align: 'right',
      // "—" with no job in the period: on "Not tied to a job", and on a type there only for
      // a year earlier.
      cell: (r) => money(r.averageJobCents),
      footer: () => money(kpis.averageJob.cents),
    },
    {
      key: 'share',
      label: 'Share',
      align: 'right',
      cell: (r): ReactNode => <Share row={r} totalCents={kpis.total.cents} />,
    },
    {
      key: 'change',
      // The header names what every change is against; the page prints those days.
      label: 'vs a year earlier',
      align: 'right',
      cell: (r) => <Change now={r.cents} yearEarlier={r.yearEarlierCents} />,
      footer: () => (
        <Change now={kpis.total.cents} yearEarlier={kpis.total.yearEarlierCents} />
      ),
    },
  ];

  return (
    <section aria-labelledby="revenue-by-type" className="min-w-0 space-y-2">
      <h2 id="revenue-by-type" className="font-heading text-sm font-semibold">
        {REVENUE_SECTION.byServiceType}
      </h2>
      {nothingInPeriod && rows.length > 0 ? notice : null}
      {/* A row opens its invoices through the table's own activator, the button in its first
          cell (never `role="button"` on the row, which would hide the cells from a screen
          reader); a click anywhere on the row does the same. */}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.key}
        onRowClick={onOpen}
        isRowClickable={(r) => opensInPeriod(r.cents, r.jobs)}
        rowLabel={(r) => `${r.label}: open its ${words.rows}`}
        rowOpens="dialog"
        footerLabel={() => 'Total'}
        empty={notice}
      />
    </section>
  );
}
